import { db, auth } from '../firebase';
import {
  doc, setDoc, deleteDoc, collection,
  onSnapshot, serverTimestamp, getDocs
} from 'firebase/firestore';
import type { HistoricalDCFResponse } from '../types/backtestTypes';

const COLLECTION = 'historicalDcf';

/**
 * Save (or overwrite) a completed historical DCF scan.
 * Keyed by ticker so re-running AMZN replaces the previous AMZN entry.
 */
export async function saveHistoricalDcfScan(
  uid: string,
  result: HistoricalDCFResponse
): Promise<void> {
  if (!uid || !result?.ticker) return;
  try {
    const ref = doc(db, COLLECTION, uid, 'scans', result.ticker.toUpperCase());
    await setDoc(ref, { ...result, savedAt: serverTimestamp() });
  } catch (err) {
    console.error('[HistoricalDcfService] Failed to save scan:', err);
  }
}

/**
 * Subscribe to all historical DCF scans for a user.
 * Returns an unsubscribe function.
 */
export function subscribeToHistoricalDcfScans(
  uid: string,
  onUpdate: (scans: HistoricalDCFResponse[]) => void
): () => void {
  if (!uid) return () => {};
  const q = collection(db, COLLECTION, uid, 'scans');
  return onSnapshot(q, (snapshot) => {
    const scans = snapshot.docs.map(d => d.data() as HistoricalDCFResponse);
    onUpdate(scans);
  }, (err) => {
    console.error('[HistoricalDcfService] Subscription error:', err);
  });
}

/**
 * Delete a single ticker's scan from history.
 */
export async function deleteHistoricalDcfScan(
  uid: string,
  ticker: string
): Promise<void> {
  if (!uid || !ticker) return;
  try {
    await deleteDoc(doc(db, COLLECTION, uid, 'scans', ticker.toUpperCase()));
  } catch (err) {
    console.error('[HistoricalDcfService] Failed to delete scan:', err);
  }
}

/**
 * Delete all scans for a user (used by the Clear button).
 */
export async function clearAllHistoricalDcfScans(uid: string): Promise<void> {
  if (!uid) return;
  try {
    const q = collection(db, COLLECTION, uid, 'scans');
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
  } catch (err) {
    console.error('[HistoricalDcfService] Failed to clear scans:', err);
  }
}
